import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { fetchCardImages, buildCardImageIndex, cardKey, type CardImage, type CardImageScope } from '../api/cardImages'

// Card art never changes, so cache the metadata forever (staleTime Infinity).
// The actual image BYTES are cached by the browser's HTTP cache on the
// immutable cards.scryfall.io URLs - this hook only caches the URL+faces map.
// `keepPrevious` (opt-in, the deck editor) keeps the last answer on screen
// while a changed name set refetches, so adding one card does not blank every
// existing row's art. Read-only surfaces leave it off.
export interface CardImagesOptions {
  keepPrevious?: boolean
}

export function useCardImages(names: string[], scope: CardImageScope = {}, options: CardImagesOptions = {}) {
  const key = [...names].map(n => n.toLowerCase()).sort().join('|')
  // A scoped gallery keys on the collection and its epoch; an unscoped one
  // keeps today's two-segment key exactly, so a deploy refetches nothing.
  const queryKey = scope.collectionId
    // eslint-disable-next-line i18next/no-literal-string -- query key
    ? ['card-images', scope.collectionId, scope.epoch ?? 0, key]
    // eslint-disable-next-line i18next/no-literal-string -- query key
    : ['card-images', key]
  return useQuery({
    queryKey,
    queryFn: () => fetchCardImages(names, scope),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: names.length > 0,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  })
}

// useCardImages + a name->CardImage lookup that resolves canonical, DFC
// front-face, and per-face names (mirrors iOS). Use `getInfo(deckCardName)`.
export function useCardImageIndex(names: string[], scope: CardImageScope = {}, options: CardImagesOptions = {}) {
  const query = useCardImages(names, scope, options)
  const index = useMemo(() => buildCardImageIndex(query.data), [query.data])
  const getInfo = useCallback(
    (name: string): CardImage | undefined => index.get(cardKey(name)),
    [index],
  )
  return { ...query, index, getInfo }
}
